import React, { useState, useContext } from 'react';
import { useScreenerExport, ExportPopupRenderer } from './components/Export';
import { UserContext } from '../../Contexts/UserContext';
import ConfirmPopup from '../../Utils/ConfirmPopup';
import xls from '../../images/ReportsImages/xls.png';
import SortIcon from 'mdi-react/SortIcon'
import SortAscendingIcon from 'mdi-react/SortAscendingIcon'
import SortDescendingIcon from 'mdi-react/SortDescendingIcon'
import { NavLink } from 'react-router-dom';
import NewLogo from '../NewLogo';
import addNoted from "../../images/AtomFavourite Copy/png/AtomFavourite Copy.png";
import addNotedActive from '../../images/AtomFavourite Copy 3/png/AtomFavourite Copy 3.png';
import UpdateColumnModal from './components/UpdateColumnModal';

// All available columns configuration
const ALL_COLUMNS = [
    { key: 'company_name', label: 'Company Name', headerClassName: "", bodyClassName: "" },
    { key: 'cin', label: 'CIN', headerClassName: "", bodyClassName: "" },
    { key: 'registered_name', label: 'Registered Name', headerClassName: "", bodyClassName: "" },
    { key: 'sector_business', label: 'Sector', headerClassName: "", bodyClassName: "" },
    { key: 'latest_deal_date', label: 'Latest Deal Date', headerClassName: "", bodyClassName: "" },
    { key: 'latest_deal_round', label: 'Latest Round', headerClassName: "", bodyClassName: "" },
    { key: 'latest_deal_amount', label: 'Latest Deal Amount ($M)', headerClassName: "", bodyClassName: "text-center" },
    { key: 'latest_company_valuation', label: 'Company Valuation (₹Cr)', headerClassName: "", bodyClassName: "text-center" },
    { key: 'latest_fy', label: 'Latest FY', headerClassName: "", bodyClassName: "" },
    { key: 'latest_revenue', label: 'Revenue (₹Cr)', headerClassName: "", bodyClassName: "text-center" },
    { key: 'latest_ebitda', label: 'EBITDA (₹Cr)', headerClassName: "", bodyClassName: "text-center" },
    { key: 'latest_pat', label: 'PAT (₹Cr)', headerClassName: "", bodyClassName: "text-center" },
    { key: 'ebitda_margin', label: 'EBITDA Margin (%)', headerClassName: "", bodyClassName: "" },
    { key: 'pat_margin', label: 'PAT Margin (%)', headerClassName: "", bodyClassName: "" },
    { key: 'operating_margin', label: 'Operating Margin (%)', headerClassName: "", bodyClassName: "" },
    { key: 'contribution_margin', label: 'Contribution Margin (%)', headerClassName: "", bodyClassName: "" },
    { key: 'revenue_growth_3y', label: 'Revenue Growth (3Y) (%)', headerClassName: "", bodyClassName: "" },
    { key: 'ebitda_growth_3y', label: 'EBITDA Growth (3Y) (%)', headerClassName: "", bodyClassName: "" },
    { key: 'pat_growth_3y', label: 'PAT Growth (3Y) (%)', headerClassName: "", bodyClassName: "" },
];

// Default visible columns
const DEFAULT_VISIBLE_COLUMNS = [
    'company_name',
    'sector',
    'latest_deal_round',
    'latest_deal_amount',
    'latest_revenue',
    'latest_ebitda',
    'latest_pat'
];

const Table = ({ totalCount, data, loading, filters = {}, hasActiveFilters, onSaveClick, onCompanyAction, screenerName, setScreenerName, screenerLists = [], selectedScreenerId, onScreenerSelect, onDeleteScreener, sortBy, setSortBy, viewMode = 'filter', onViewModeChange }) => {
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [tempSelectedColumns, setTempSelectedColumns] = useState(visibleColumns);
    const [actionLoading, setActionLoading] = useState({});
    const [isEditingName, setIsEditingName] = useState(false);
    const [isScreenerDropdownOpen, setIsScreenerDropdownOpen] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState({ accept: false, reject: false, shortlist: false });
    const { authAlert } = useContext(UserContext);
    
    const { 
        isExporting, 
        loading: exportLoading,
        authCode,
        setAuthCode,
        reachedLimitPopup,
        alert,
        showExportAlert,
        onAlertChange,
        handleAuthCodeSubmit,
        closePopup
    } = useScreenerExport({ fileName: 'screener_data', filters, viewMode, selectedScreenerId });

    const MAX_COLUMNS = 8;

    // Handle column toggle in edit modal
    const handleColumnToggle = (columnKey) => {
        setTempSelectedColumns((prev) => {
            if (prev.includes(columnKey)) {
                return prev.filter((key) => key !== columnKey);
            }
            // Prevent adding more than MAX_COLUMNS
            if (prev.length >= MAX_COLUMNS) {
                return prev;
            }
            return [...prev, columnKey];
        });
    };

    // Apply column changes
    const handleApplyChanges = () => {
        setVisibleColumns(tempSelectedColumns);
        setIsEditModalOpen(false);
    };

    // Cancel and reset
    const handleCancel = () => {
        setTempSelectedColumns(visibleColumns);
        setIsEditModalOpen(false);
    };

    // Open edit modal
    const handleOpenEditModal = () => {
        setTempSelectedColumns(visibleColumns);
        setIsEditModalOpen(true);
    };

    // Reset to default columns
    const handleResetToDefault = () => {
        setTempSelectedColumns(DEFAULT_VISIBLE_COLUMNS);
    };

    // Get visible column configs in order
    const getVisibleColumnConfigs = () => {
        return ALL_COLUMNS.filter((col) => visibleColumns.includes(col.key));
    };

    // Format cell value
    const formatCellValue = (value, key) => {
        if (value === null || value === undefined) return '-';
        
        // Format numeric values
        if (typeof value === 'number') {
            if (key.includes('margin') || key.includes('growth')) {
                return `${value.toFixed(2)}`;
            }
            if (key.includes('amount') || key.includes('revenue') || key.includes('ebitda') || key.includes('pat')) {
                return `${value.toFixed(2)}`;
            }
            if(key.includes('valuation')){
                return value !== 0 ? value : "-"
            }
            return value.toFixed(2);
        }
        
        return value;
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-16 text-slate-500">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
                <p className="mt-4">Loading data...</p>
            </div>
        );
    }

    const exportHandler = () => {
        showExportAlert();
    }

    const handleAction = async (companyIds, action) => {
        // For single row action
        if (companyIds.length === 1) {
            setActionLoading(prev => ({ ...prev, [`${companyIds[0]}-${action}`]: true }));
        }
        try {
            await onCompanyAction(companyIds, action);
        } finally {
            if (companyIds.length === 1) {
                setActionLoading(prev => ({ ...prev, [`${companyIds[0]}-${action}`]: false }));
            }
        }
    };

    // Handle bulk action
    const handleBulkAction = async (action, customIds = null) => {
        const idsToUse = customIds || selectedRows;
        console.log(action, selectedRows, "select bulk")
        if (idsToUse.length === 0) return;
        setBulkActionLoading(prev => ({ ...prev, [action]: true }));
        try {
            await onCompanyAction(idsToUse, action);
            setSelectedRows([]); // Clear selection after bulk action
        } finally {
            setBulkActionLoading(prev => ({ ...prev, [action]: false }));
        }
    };

    // Handle row selection
    const handleRowSelect = (companyId) => {
        setSelectedRows(prev => {
            if (prev.includes(companyId)) {
                return prev.filter(id => id !== companyId);
            }
            return [...prev, companyId];
        });
    };

    // Handle select all (only selectable rows with actionable status)
    const getSelectableRows = () => {
        if (!data) return [];
        // Selectable rows are those with Pending or Shortlisted status
        return data.filter(row => row.status === 'Pending' || row.status === 'Approve/Reject').map(row => row.company_id);
    };

    const handleSelectAll = () => {
        const selectableRows = getSelectableRows();
        if (selectedRows.length === selectableRows.length && selectableRows.length > 0) {
            setSelectedRows([]);
        } else {
            setSelectedRows(selectableRows);
        }
    };

    const isAllSelected = () => {
        const selectableRows = getSelectableRows();
        return selectableRows.length > 0 && selectedRows.length === selectableRows.length;
    };

    const isSomeSelected = () => {
        const selectableRows = getSelectableRows();
        return selectedRows.length > 0 && selectedRows.length < selectableRows.length;
    };

    // Handle column sort
    const handleSort = (columnKey) => {
        setSortBy(prev => {
            if (prev.sort_by === columnKey) {
                // Toggle sort order if same column
                return {
                    sort_by: columnKey,
                    sort_order: prev.sort_order === 'asc' ? 'desc' : 'asc'
                };
            }
            // Default to desc for new column
            return {
                sort_by: columnKey,
                sort_order: 'desc'
            };
        });
    };

    // Render sort icon
    const renderSortIcon = (columnKey) => {
        // const isActive = sortBy?.sort_by === columnKey;
        // const isAsc = sortBy?.sort_order === 'asc';
        
        // return (
        //     <span className={`ml-1 inline-flex flex-col text-[10px] leading-none ${isActive ? 'text-[#bc8b20]' : 'text-slate-400'}`}>
        //         <span className={`${isActive && isAsc ? 'text-[#bc8b20]' : 'text-slate-300'}`}><SortDescendingIcon className="pl-2 block h-[15px]" /></span>
        //         <span className={`${isActive && !isAsc ? 'text-[#bc8b20]' : 'text-slate-300'}`}><SortAscendingIcon className="pl-2 block h-[15px]" /></span>
        //     </span>
        // );
        
        if (columnKey == sortBy.sort_by) {
            if (sortBy.sort_order == 'asc') {
            return <SortDescendingIcon className="pl-2 block h-[15px]" />
            }
            return <SortAscendingIcon className="pl-2 block h-[15px]" />
        } else {
            return <SortIcon className="pl-2 block h-[15px] " />
        }
    };

    return (
        <div className="p-4 font-sans">
            {/* Confirm Popup */}
            <ConfirmPopup 
                {...alert} 
                onChange={onAlertChange} 
                icon={<></>} 
                loading={exportLoading} 
                activeBtnName={isExporting ? "Downloading" : "Download"} 
                closeBtnName='Close' 
                title='Export Data' 
                disable={isExporting} 
            />
            {/* Export Popups */}
            <ExportPopupRenderer
                reachedLimitPopup={reachedLimitPopup}
                authCode={authCode}
                setAuthCode={setAuthCode}
                handleAuthCodeSubmit={handleAuthCodeSubmit}
                closePopup={closePopup}
                authAlert={authAlert}
            />
            {/* Header with Edit Button */}
            <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-3">
                    {/* Screener Selector Box */}
                    
                        <div className="relative">
                            <button
                                className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 border border-slate-300 cursor-pointer text-sm font-medium text-slate-700 hover:bg-slate-200 transition-colors duration-150"
                                onClick={() => setIsScreenerDropdownOpen(!isScreenerDropdownOpen)}
                            >
                                <span className="text-xs">📋</span>
                                <span className="max-w-[400px] truncate">
                                    {screenerLists.find(s => s.id === selectedScreenerId)?.name || screenerName || 'Untitled'}
                                </span>
                                <span className={`text-xs transition-transform duration-150 ${isScreenerDropdownOpen ? 'rotate-180' : ''}`}>▼</span>
                            </button>
                            {isScreenerDropdownOpen && (
                                <>
                                    <div 
                                        className="fixed inset-0 z-40" 
                                        onClick={() => setIsScreenerDropdownOpen(false)}
                                    />
                                    <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 min-w-[200px] max-h-[250px] overflow-y-auto">
                                        {screenerLists.map((screener) => (
                                            <div
                                                key={screener.id}
                                                className={`px-4 py-2.5 cursor-pointer text-sm hover:bg-slate-50 transition-colors duration-150 flex items-center justify-between gap-2 ${
                                                    selectedScreenerId === screener.id ? 'bg-[#474747]/10 text-black font-medium' : 'text-slate-700'
                                                }`}
                                            >
                                                <div 
                                                    className="flex items-center gap-2 flex-1 min-w-0"
                                                    onClick={() => {
                                                        onViewModeChange("filter")
                                                        onScreenerSelect(screener.id);
                                                        setIsScreenerDropdownOpen(false);
                                                    }}
                                                >
                                                    {selectedScreenerId === screener.id && <span className="text-xs">✓</span>}
                                                    <span className="truncate">{screener.name}</span>
                                                </div>
                                                <button
                                                    className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors duration-150 text-xs"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (window.confirm(`Are you sure you want to delete "${screener.name}"?`)) {
                                                            onDeleteScreener(screener.id);
                                                        }
                                                    }}
                                                    title="Delete screener"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    <span className="text-xs text-slate-400 cursor-pointer" onClick={onSaveClick}>✏️</span> 
                    {/* View Mode Toggle */}
                    <div className="flex items-center bg-white rounded-3xl p-0.5 border border-black">
                        {[
                            { key: 'filter', label: 'Filtered' },
                            { key: 'shortlisted', label: 'Shortlisted' },
                            { key: 'approved', label: 'Approved' }
                        ].map((mode) => (
                            <button
                                key={mode.key}
                                className={`px-3 py-1.5 text-sm font-medium rounded-3xl transition-colors duration-150 ${
                                    viewMode === mode.key 
                                        ? 'bg-[#696969] text-white shadow-sm' 
                                        : 'text-slate-600 hover:text-slate-800 hover:bg-slate-200'
                                }`}
                                onClick={() => selectedScreenerId !== null ? onViewModeChange(mode.key) : null}
                                disabled={selectedScreenerId === null}
                            >
                                {mode.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className='flex gap-2 items-center'>
                    {/* Bulk Action Buttons */}
                    {selectedRows.length > 0 ? (
                        <>
                            <span className="text-sm text-slate-600 font-medium">
                                {selectedRows.length} selected
                            </span>
                            {/* Show Bulk Shortlist if any Pending rows are selected */}
                            {data?.some(row => selectedRows.includes(row.company_id) && row.status === 'Pending') && (
                                <button 
                                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 border border-blue-200 rounded-lg cursor-pointer text-sm font-medium transition-colors duration-200 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => {
                                        const pendingRows = data.filter(row => selectedRows.includes(row.company_id) && row.status === 'Pending').map(row => row.company_id);
                                        handleBulkAction('shortlist', pendingRows);
                                    }}
                                    disabled={bulkActionLoading.shortlist}
                                >
                                    {bulkActionLoading.shortlist ? '...' : 'Bulk Shortlist'}
                                </button>
                            )}
                            {/* Show Bulk Approve if any Shortlisted rows are selected */}
                            {data?.some(row => selectedRows.includes(row.company_id) && row.status === 'Approve/Reject') && (
                                <>
                                    <button 
                                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg cursor-pointer text-sm font-medium transition-colors duration-200 hover:bg-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={() => {
                                            const shortlistedRows = data.filter(row => selectedRows.includes(row.company_id) && row.status === 'Approve/Reject').map(row => row.company_id);
                                            handleBulkAction('accept', shortlistedRows);
                                        }}
                                        disabled={bulkActionLoading.accept}
                                    >
                                        {bulkActionLoading.accept ? '...' : 'Bulk Approve'}
                                    </button>
                                    <button 
                                        className="flex items-center gap-1 px-3 py-1.5 bg-rose-100 text-rose-700 border border-rose-200 rounded-lg cursor-pointer text-sm font-medium transition-colors duration-200 hover:bg-rose-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={() => handleBulkAction('reject')}
                                        disabled={bulkActionLoading.reject}
                                    >
                                        {bulkActionLoading.reject ? '...' : 'Bulk Reject'}
                                    </button>
                                </>
                            )}
                            <div className="w-px h-6 bg-slate-300 mx-1"></div>
                        </>
                    ) : hasActiveFilters(filters) && (
                        <span className="text-sm text-slate-600 font-medium">
                                {totalCount} Companies Found
                        </span>
                    )}
                    <button 
                        className="flex items-center gap-2 px-2 py-1.5 pr-3 bg-[#696969] text-[#f2cf82] border-none rounded-lg cursor-pointer text-sm font-medium transition-colors duration-200 hover:bg-[#474747]"
                        onClick={onSaveClick}
                    >
                        Save
                    </button>
                    <button 
                        className="flex items-center gap-2 px-2 py-1.5 pr-3 bg-[#696969] text-[#f2cf82] border-none rounded-lg cursor-pointer text-sm font-medium transition-colors duration-200 hover:bg-[#474747]"
                        onClick={handleOpenEditModal}
                        >
                        Edit Columns
                    </button>
                    {totalCount > 0 && <img
                        src={xls}
                        className={`h-[24px] w-[21px] ${isExporting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        alt="Export"
                        onClick={isExporting ? undefined : exportHandler}
                    />}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-lg shadow-sm">
                <table className="w-full border-collapse bg-white">
                    <thead>
                        <tr>
                            {/* Select All Checkbox */}
                            {viewMode !== "approved" && <th className="px-2 py-3.5 text-center bg-slate-50 border-b-2 border-slate-200 w-10">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected()}
                                    ref={el => {
                                        if (el) el.indeterminate = isSomeSelected();
                                    }}
                                    onChange={handleSelectAll}
                                    className="w-4 h-4 cursor-pointer accent-black"
                                    title="Select all"
                                />
                            </th>}
                            {getVisibleColumnConfigs().map((col) => (
                                <th 
                                    key={col.key} 
                                    className={`px-2 py-3.5 text-left bg-slate-50 border-b-2 border-slate-200 font-semibold text-xs text-slate-600 uppercase tracking-wide whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors duration-150 select-none ${col.headerClassName}`}
                                    onClick={() => handleSort(col.key)}
                                >
                                    <span className="flex items-center">
                                        {col.label}
                                        {renderSortIcon(col.key)}
                                    </span>
                                </th>
                            ))}
                            {viewMode !== "approved" && <th className="w-[8%] px-2 py-3.5 text-center bg-slate-50 border-b-2 border-slate-200 font-semibold text-xs text-slate-600 uppercase tracking-wide whitespace-nowrap">
                                {viewMode === "filter" ? "Shortlist" : "Actions"}
                            </th>}
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.length > 0 ? (
                            data.map((row, index) => (
                                <tr 
                                    key={row.cin || index} 
                                    className={`${index % 2 === 0 ? 'bg-white' : 'bg-white'} ${selectedRows.includes(row.company_id) ? 'bg-[#bc8b20]/5' : ''} hover:bg-slate-100 transition-colors duration-150`}
                                >
                                    {/* Row Checkbox */}
                                    {viewMode !== "approved" && <td className="px-2 py-3 border-b border-slate-200 text-center">
                                        {(row.status === 'Pending' || row.status === "Approve/Reject" || row.status === 'Shortlisted') ? (
                                            <input
                                                type="checkbox"
                                                checked={selectedRows.includes(row.company_id)}
                                                onChange={() => handleRowSelect(row.company_id)}
                                                className="w-4 h-4 cursor-pointer accent-[#bc8b20]"
                                            />
                                        ) : (
                                            <input
                                                type="checkbox"
                                                disabled
                                                className="w-4 h-4 cursor-not-allowed opacity-30"
                                            />
                                        )}
                                    </td>}
                                    {getVisibleColumnConfigs().map((col) => (
                                        <td 
                                            key={col.key} 
                                            className={`px-2 py-3 border-b border-slate-200 text-sm text-slate-800 ${col.bodyClassName}`}
                                        >
                                            {col.key == "company_name" ? 
                                            <div className='relative flex items-center gap-1'>
                                                <div>
                                                    <NavLink to={`/pe/company/${row.company_id}`} target='_blank' className={"flex items-center gap-1"}>
                                                        <NewLogo menu='pe' name={row?.company_name} id={row?.company_id} />
                                                            {formatCellValue(row[col.key], col.key)}
                                                    </NavLink> 
                                                </div>
                                                {row.new && <span 
                                                    className="rounded-md bg-[#f3e8ff] px-2 py-[3px] text-xs font-sans_book_body text-[#333333]"
                                                >
                                                    New
                                                </span>}
                                            </div>
                                            : formatCellValue(row[col.key], col.key)}
                                        </td>
                                    ))}
                                    {viewMode !== "approved" && <td className="px-2 py-3 border-b border-slate-200 text-sm">
                                        {row.status === 'Pending' ? (
                                            <div className="flex gap-2 justify-center">
                                                <button
                                                    className="px-3 py-1.5 rounded cursor-pointer text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                                                    onClick={() => handleAction([row.company_id], 'shortlist')}
                                                    disabled={actionLoading[`${row.company_id}-shortlist`]}
                                                >
                                                    <img src={actionLoading[`${row.company_id}-shortlist`] ? "..." : addNoted  } alt='add to list' className='w-[24px] h-[22px]'/>
                                                    {/* {actionLoading[`${row.company_id}-shortlist`] ? '...' : 'Shortlist'} */}
                                                </button>
                                            </div>
                                        ) : row.status === 'Approve/Reject' ? (
                                            <div className="flex gap-2 justify-center">
                                                <button
                                                    className="px-3 py-1.5 bg-emerald-100 text-emerald-700 border border-emerald-200 rounded cursor-pointer text-xs font-medium hover:bg-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                                                    onClick={() => handleAction([row.company_id], 'accept')}
                                                    disabled={actionLoading[`${row.company_id}-accept`]}
                                                >
                                                    {actionLoading[`${row.company_id}-accept`] ? '...' : 'Approve'}
                                                </button>
                                                <button
                                                    className="px-3 py-1.5 bg-rose-100 text-rose-700 border border-rose-200 rounded cursor-pointer text-xs font-medium hover:bg-rose-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                                                    onClick={() => handleAction([row.company_id], 'reject')}
                                                    disabled={actionLoading[`${row.company_id}-reject`]}
                                                >
                                                    {actionLoading[`${row.company_id}-reject`] ? '...' : 'Reject'}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex justify-center">
                                                <span className={`px-3 py-1.5 rounded text-xs font-medium ${
                                                    row.status === 'Approved' ? 'bg-green-100 text-green-700' : 
                                                    row.status === 'Rejected' ? 'bg-red-100 text-red-700' : 
                                                    ''
                                                }`}>
                                                {row.status === "Shortlisted" ? <img src={addNotedActive} alt='add to list' className='w-[24px] h-[22px]'/> : row.status}
                                                </span>
                                            </div>
                                        )}
                                    </td>}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td 
                                    colSpan={visibleColumns.length + 2} 
                                    className="p-10 text-center text-slate-500 text-sm"
                                >
                                    {`${hasActiveFilters(filters) ? "No data available" : "Select any filters to see the results"}`}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Edit Columns Modal */}
            {isEditModalOpen && (
                <UpdateColumnModal 
                    ALL_COLUMNS={ALL_COLUMNS} 
                    tempSelectedColumns={tempSelectedColumns}
                    handleCancel={handleCancel} 
                    handleApplyChanges={handleApplyChanges} 
                    handleResetToDefault={handleResetToDefault}
                    handleColumnToggle={handleColumnToggle}
                />
            )}
        </div>
    );
};

export default Table;